import { Dropdown, Menu, message, Table, Tag, Row, Input, Select } from "antd";
import { SearchOutlined, MoreOutlined } from "@ant-design/icons";

import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API } from "aws-amplify";
import exportCSV from "assets/icons/exportcsv.svg";
import { listEpcDependents, listEpcUsers } from "graphql/customEpcQuery";
import { callGraphQLBatch } from "utils/api";
import TableSkeleton from "utility/TableSkeleton";
import search from "utility/search";
import { CSVLink } from "react-csv";
import { Option } from "antd/lib/mentions";
import CommonTable from "../commonComponents/Table";
import CommonCSVExport from "../commonComponents/CSVExport";
import CommonSearch from "../commonComponents/Search";

interface UserData {
  key: string;
  email: string;
  doctorName: string;
  name: string;
  role: string;
  billingStatus: any;
  Action: string;
  createdAt: string;
  status: string;
}

const billingStatusFilter = ["PAID", "UNPAID", "CANCELLED", "FAILED"];

export default function AllUsersListing() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userData, setUserData] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<UserData[]>([]);
  const [dpcBillingFilter, setDpcBillingFilter] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");

  const [messageApi, contextHolder] = message.useMessage();

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      width: 180,
    },
    {
      title: "Email",
      dataIndex: "email",
      width: 220,
    },
    {
      title: "Doctor",
      dataIndex: "doctorName",
      width: 180,
    },

    {
      title: "Role",
      dataIndex: "role",
      width: 120,
    },
    {
      title: "Billing Status",
      dataIndex: "billingStatus",
      width: 140,
    },
    // {
    //   title: "",
    //   dataIndex: "Action",
    //   className: "actions",
    //   width: 50,
    //   render: (id: String) => {
    //     return (
    //       <Dropdown
    //         overlay={
    //           <Menu>
    //             <Menu.Item key={1}>
    //               {/* <Link to="/doctor-detail?isDpcDoctor=true" state={{ id: id }}> */}
    //               WIP
    //               {/* </Link> */}
    //             </Menu.Item>
    //           </Menu>
    //         }
    //         overlayClassName="download-btn"
    //         placement="bottomRight"
    //       >
    //         <MoreOutlined />
    //       </Dropdown>
    //     );
    //   },
    // },
  ];

  const fetchAllUsers = useCallback(async () => {
    try {
      setLoading(true);
      const filter = {
        dpcDoctorID: { attributeExists: true },
      };
      const [users, dependents] = await callGraphQLBatch([
        {
          query: listEpcUsers,
          authMode: "AMAZON_COGNITO_USER_POOLS",
          variables: {
            filter,
          },
          outputKeyword: "listEmployeeRegis",
        },
        {
          query: listEpcDependents,
          authMode: "AMAZON_COGNITO_USER_POOLS",
          variables: {
            filter,
          },
          outputKeyword: "listMembers",
        },
      ]);

      // Merge and map data
      const merged = [...(users || []), ...(dependents || [])].map((item) => ({
        key: item.id,
        email: item.emailID,
        doctorName: item.dpcDoctor?.doctorName || "-",
        name: `${item.firstName || ""} ${item.lastName || ""}` || "-",
        role: item?.epcEmployerID
          ? "Employee"
          : item?.dpcEmployeeID
          ? "Dependent"
          : "Individual",
        status:
          item?.DpcDetails?.accountStatus === "ACTIVE"
            ? "PAID"
            : item?.DpcDetails?.accountStatus === "CANCELLED"
            ? "CANCELLED"
            : item?.DpcDetails?.accountStatus === "FAILED"
            ? "FAILED"
            : item?.DpcDetails?.accountStatus === null ||
              item?.DpcDetails?.accountStatus === ""
            ? "UNPAID"
            : "UNPAID",
        billingStatus:
          item?.DpcDetails?.accountStatus === "ACTIVE" ? (
            <Tag color="green">PAID</Tag>
          ) : item?.DpcDetails?.accountStatus === "CANCELLED" ? (
            <Tag color="volcano">CANCELLED</Tag>
          ) : item?.DpcDetails?.accountStatus === "FAILED" ? (
            <Tag color="red">FAILED</Tag>
          ) : item?.DpcDetails?.accountStatus === null ||
            item?.DpcDetails?.accountStatus === "" ? (
            <Tag color="orange">UNPAID</Tag>
          ) : (
            <Tag color="orange">UNPAID</Tag>
          ),
        Action: item.id,
        createdAt: item.createdAt,
      }));

      // Sort by createdAt descending
      merged.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setDataSource(merged);
      setUserData(merged);
    } catch (error) {
      messageApi.open({
        type: "error",
        content: "Something went wrong",
      });
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllUsers();
  }, [fetchAllUsers]);

  const headers = [
    { label: "Member Email", key: "email" },
    { label: "Member Name", key: "name" },
    { label: "Role", key: "role" },
    { label: "Doctor Name", key: "doctorName" },
    { label: "Billing Status", key: "dpcBillingStatus" },
  ];

  const handleSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const currValue = e.target.value.trim();
    setSearchValue(e.target.value);
    const searchFields = ["email", "doctorName", "name", "role"];
    const filteredData = search(userData, currValue, searchFields);
    const filtered = filterByBillingStatus(dpcBillingFilter, filteredData);
    setDataSource(filtered);
  };

  const filterByBillingStatus = (status: string | null, data: UserData[]) => {
    const statusMap = {
      PAID: "PAID",
      UNPAID: "UNPAID",
      CANCELLED: "CANCELLED",
      FAILED: "FAILED",
    };
    return status && statusMap[status as keyof typeof statusMap]
      ? data.filter(
          (emp) => emp.status === statusMap[status as keyof typeof statusMap]
        )
      : data;
  };

  const handleBillingFilter = (value: string | null) => {
    setLoading(true);
    setDpcBillingFilter(value || null);
    setSearchValue("");
    if (value && billingStatusFilter.includes(value)) {
      const filtered = filterByBillingStatus(value, userData);
      setDataSource(filtered);
    } else {
      setDataSource(userData);
    }

    setLoading(false);
  };

  function getCsvExportData() {
    return dataSource.map((row) => {
      const {
        email,
        name = "-",
        doctorName = "-",
        role = "-",
        billingStatus,
      } = row || {};

      return {
        email,
        name,
        doctorName,
        role,
        dpcBillingStatus: billingStatus?.props?.children || "", // Extract text from Tag
      };
    });
  }

  return (
    <>
      {contextHolder}

      {loading ? (
        <TableSkeleton />
      ) : (
        <>
          <Row justify="space-between" className="table-header">
            <Row align="middle">
              <CommonSearch onChange={handleSearch} value={searchValue} />
            </Row>
            <Row className="gap-10 mtr-20 bold-font filter-dropdown-list">
              <Select
                allowClear
                placeholder="Billing Status"
                style={{ width: 150 }}
                onChange={handleBillingFilter}
                getPopupContainer={(trigger) => trigger.parentNode}
                // value={dpcBillingFilter || undefined}
              >
                <Option value="PAID">Paid</Option>
                <Option value="UNPAID">Unpaid</Option>
                <Option value="FAILED">Failed</Option>
                <Option value="CANCELLED">Cancelled</Option>
              </Select>

              <CommonCSVExport
                data={getCsvExportData()}
                headers={headers}
                filename={`All Members.csv`}
                onClick={() =>
                  messageApi.open({
                    type: "loading",
                    content: "Exporting CSV...",
                    duration: 1,
                  })
                }
              />
            </Row>{" "}
          </Row>
          <CommonTable
            columns={columns}
            dataSource={dataSource}
            loading={loading}
            className="common-table"
            scroll={{ x: 992 }}
            pagination={{
              position: ["bottomCenter"],
              defaultPageSize: 100,
            }}
          />
        </>
      )}
    </>
  );
}
